import { FormControl, FormControlLabel, FormGroup, FormHelperText, FormLabel, Switch } from "@mui/material"
import { checkTouched, isTouched as hasBeenTouched } from "form/tools/validation"
import { Field, FieldProps } from "formik"
import React from "react"
import { IOption } from "ui/model"

interface IFieldCheckboxesProps<T extends string | number> {
  fieldName: string // ex: "main.order_id"
  label: string
  options: IOption[]
  value: T[]
  disabled?: boolean
  isReadOnly?: boolean
  sx?: any
  error?: string
  required?: boolean
}

export const FieldCheckboxes = React.memo(
  ({
    fieldName,
    label,
    options,
    value,
    disabled,
    isReadOnly,
    error,
    sx,
    required
  }: IFieldCheckboxesProps<string | number>): JSX.Element => (
    <Field name={fieldName}>
      {({ form: { touched, setFieldValue } }: FieldProps) => {
        const isTouched: boolean = hasBeenTouched(touched, fieldName)
        const isDisabled: boolean = disabled || Boolean(isReadOnly)
        const hasError = isTouched ? error !== undefined && error !== "" : false
        return (
          <FormControl
            id={`${fieldName}-checkboxes-field`}
            required={required}
            disabled={isDisabled}
            error={hasError}
            component="fieldset"
            variant="standard"
            sx={{
              "& legend": { fontSize: ".9rem" },
              "& legend.Mui-focused": { color: theme => theme.palette.text.primary },
              "& legend.Mui-error": { color: theme => theme.palette.error.main },
              "& span.MuiFormControlLabel-label": { fontSize: ".85rem" },
              ...sx
            }}>
            <FormLabel component="legend">{label}</FormLabel>
            <FormGroup>
              {options.map((o, index) => (
                <FormControlLabel
                  key={index}
                  control={
                    <Switch
                      checked={value.indexOf(o.value) !== -1}
                      sx={{ ml: "1rem" }}
                      disabled={isDisabled}
                      onChange={(_event, checked) => {
                        checkTouched(touched, fieldName)
                        setFieldValue(
                          fieldName,
                          checked && value.indexOf(o.value) === -1
                            ? [...value, o.value]
                            : value.filter(v => v !== o.value)
                        )
                      }}
                    />
                  }
                  label={o.label}
                />
              ))}
            </FormGroup>
            {error && <FormHelperText id={`${fieldName}-error`}>{error}</FormHelperText>}
          </FormControl>
        )
      }}
    </Field>
  )
)
